import { useQuery } from "@tanstack/react-query";
import { CDN_BASE_URL } from "pages/whats-new/config";

// Org whitelist controlling the post-login redirect to /creator-profile,
// hosted as teacher-onboarding.json in the LibertyFi/whats-new repo and served
// over the same jsDelivr CDN as the What's New content. Editing that file rolls
// the redirect out to more orgs without a frontend deploy (jsDelivr caches
// @main refs, so allow up to ~12h to propagate).
const TEACHER_ONBOARDING_WHITELIST_URL = `${CDN_BASE_URL}/whitelist/teacher-onboarding.json`;

const STALE_TIME = 5 * 60 * 1000;

type TeacherOnboardingWhitelistConfig = {
  enabledOrganizationIds?: string[];
};

// jsDelivr serves these with `max-age=604800`, so a browser that fetched the
// file once would keep the stale list for a week — long enough to defeat the
// no-deploy rollout above. `no-cache` revalidates instead of disabling the
// cache: the etag makes the answer a cheap 304 when nothing changed. It only
// fixes the browser hop; the edge still holds its own copy for ~12h.
const fetchTeacherOnboardingWhitelist = async (
  signal?: AbortSignal
): Promise<string[]> => {
  const res = await fetch(TEACHER_ONBOARDING_WHITELIST_URL, {
    signal,
    cache: "no-cache",
  });
  if (!res.ok) {
    throw new Error(
      `Failed to load teacher onboarding whitelist (${res.status})`
    );
  }
  const json = (await res.json()) as TeacherOnboardingWhitelistConfig;
  return json.enabledOrganizationIds ?? [];
};

/**
 * Org ids whose users get redirected to /creator-profile after login.
 * `enabledOrgIds` resolves to [] while loading or on error, so the redirect
 * rule simply doesn't fire rather than blocking navigation.
 */
export const useTeacherOnboardingWhitelist = (): {
  enabledOrgIds: string[];
  isLoading: boolean;
} => {
  const { data, isLoading } = useQuery({
    queryKey: ["teacher-onboarding", "whitelist"],
    queryFn: ({ signal }) => fetchTeacherOnboardingWhitelist(signal),
    staleTime: STALE_TIME,
    retry: 1,
  });
  return { enabledOrgIds: data ?? [], isLoading };
};
